import React, { memo, useCallback, useMemo } from 'react';

import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import Button from '@atlaskit/button/new';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- TODO: migrate to @atlaskit/primitives/compiled
import Inline from '@atlaskit/primitives/inline';
import type { SendAnalyticsEvent } from '@atlassian/conversation-assistant-instrumentation/types/analytics';
import { ModalButtonTrigger } from '@atlassian/entry-points/modal-button-trigger';
import {
	CreateWorkItemsButton,
	type OnClickErrorProps,
	type OnClickSuccessProps,
} from '@atlassian/jira-create-work-items/createButton.tsx';
import { useGetDraftWorkItemsToCreate } from '@atlassian/jira-create-work-items/draft-work-items-store/hooks.tsx';
import type { DraftWorkItem } from '@atlassian/jira-create-work-items/useAiCreateProjectContext/types.tsx';

import type { GicModalProps } from '../../../../common/ui/gic-modal';
import { gicModalEntryPoint } from '../../../../common/ui/gic-modal/entrypoint/gicModal.entrypoint';
import {
	isSubTask,
	isSubTaskWithoutCreatedParent,
} from '../../../../common/utils/jira-create-with-rovo-preview-card';

import type { footerActions_AtlassianConversationAssistantPreviewCard_PreviewFooterActions_project$key as ProjectKey } from './__generated__/footerActions_AtlassianConversationAssistantPreviewCard_PreviewFooterActions_project.graphql';
import type { footerActions_AtlassianConversationAssistantPreviewCard_PreviewFooterActions_query$key as EnabledKey } from './__generated__/footerActions_AtlassianConversationAssistantPreviewCard_PreviewFooterActions_query.graphql';

const messages = defineMessages({
	singleCreate: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.create-in-project',
		description: 'The label for the button to create the work item for the Jira AIFC experience',
		defaultMessage: 'Create',
	},
	createAll: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.create-all',
		description:
			'The label for the button to create all the multi work items for the Jira AIFC experience',
		defaultMessage: 'Create all',
	},
	createCount: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.create-count',
		description:
			'The label for the button to create multiple selected work items for the Jira AIFC experience',
		defaultMessage: 'Create {count}',
	},
	singleDismissAction: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.dismiss-action',
		description: 'The label for the button to dismiss the work items for the Jira AIFC experience',
		defaultMessage: 'Dismiss',
	},
	dismissAllAction: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.dismiss-all-action',
		description:
			'The label for the button to dismiss all the multi work items for the Jira AIFC experience',
		defaultMessage: 'Dismiss all',
	},
	dismissAllActionAccessibleLabel: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.dismiss-all-action-accessible-label',
		description:
			'Accessible label for the button to dismiss all the multi work items for the Jira AIFC experience, providing additional context for screen reader users',
		defaultMessage: 'Dismiss all work items',
	},
	singleDismissActionAccessibleLabel: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.dismiss-action-accessible-label',
		description:
			'Accessible label for the button to dismiss the work item for the Jira AIFC experience, providing additional context for screen reader users',
		defaultMessage: 'Dismiss work item',
	},
	openPreview: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.open-preview',
		description:
			'The label for the button to open the multi work item preview card for the Jira AIFC experience',
		defaultMessage: 'Preview',
	},
	openPreviewAccessibleLabel: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.open-preview-accessible-label',
		description:
			'Accessible label for the button to open the multi work item preview card for the Jira AIFC experience, providing additional context for screen reader users',
		defaultMessage: 'Preview work items',
	},
	viewingNow: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.viewing-now',
		description:
			'The label for the button to view the multi work item preview card for the Jira AIFC experience',
		defaultMessage: 'Viewing now',
	},
	refineInGic: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.edit-in-gic',
		description: 'The label for the button to edit the work item in the GIC',
		defaultMessage: 'Refine',
	},
	refineWorkItemAccessibleLabel: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.refine-work-item-accessible-label',
		description:
			'Accessible label for the button to refine the work item in the GIC, providing additional context for screen reader users',
		defaultMessage: 'Refine work item',
	},
	createWorkItemAccessibleLabel: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.create-work-item-accessible-label',
		description:
			'Accessible label for the button to create the work item for the AIFC experience, providing additional context for screen reader users',
		defaultMessage: 'Create work item',
	},
	createAllWorkItemsAccessibleLabel: {
		id: 'ai-mate.conversation-assistant-preview-card.jira-preview-card.create-all-work-items-accessible-label',
		description:
			'Accessible label for the button to create all work items for the AIFC experience, providing additional context for screen reader users',
		defaultMessage: 'Create all work items',
	},
});

type PreviewFooterActionsProps = {
	product?: string;
	query: EnabledKey;
	project: ProjectKey;
	cloudId: string;
	messageId: string;
	projectIdOrKey: string;
	isSingleWorkItem?: boolean;
	areButtonsDisabled?: boolean;
	onDismiss: () => void;
	onCreateSuccess: (args: OnClickSuccessProps) => void;
	onCreateError: (args: OnClickErrorProps) => void;
	onPreviewClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	gicEntryPointProps: GicModalProps;
	onBulkAccept: () => void;
	isStagingArea: boolean;
	entryPoint?: string;
	showSuccessFlagOnCreate?: boolean;
	selectedItems?: Set<string>;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
};

const DeclineButton = ({
	areButtonsDisabled,
	isSingleWorkItem,
	onDismiss,
}: {
	areButtonsDisabled: boolean;
	isSingleWorkItem: boolean;
	onDismiss: () => void;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Button
			appearance="subtle"
			onClick={onDismiss}
			isDisabled={areButtonsDisabled}
			aria-label={
				FeatureGates.getExperimentValue('jira-aifc-a11y-fixes-preview-card', 'isEnabled', false, {
					fireExperimentExposure: false,
				})
					? formatMessage(
							isSingleWorkItem
								? messages.singleDismissActionAccessibleLabel
								: messages.dismissAllActionAccessibleLabel,
						)
					: undefined
			}
		>
			<FormattedMessage
				{...(isSingleWorkItem ? messages.singleDismissAction : messages.dismissAllAction)}
			/>
		</Button>
	);
};

const EditInGicButton = ({
	areButtonsDisabled,
	gicEntryPointProps,
	selectedDraftWorkItem,
	projectId,
	isStagingArea,
	sendAifcAnalyticsEvent,
}: {
	areButtonsDisabled: boolean;
	gicEntryPointProps: GicModalProps;
	selectedDraftWorkItem?: DraftWorkItem;
	projectId?: string;
	isStagingArea: boolean;
	sendAifcAnalyticsEvent?: SendAnalyticsEvent;
}) => {
	const { formatMessage } = useIntl();
	const isSubTaskWithoutParent = selectedDraftWorkItem
		? isSubTaskWithoutCreatedParent(selectedDraftWorkItem)
		: false;

	const onGicClick = useCallback(() => {
		sendAifcAnalyticsEvent?.({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'createWorkItemsEditInGic',
			attributes: {
				itemCount: 1,
				isSingleWorkItem: true,
				isStagingArea,
			},
		});
	}, [sendAifcAnalyticsEvent, isStagingArea]);

	if (!selectedDraftWorkItem) {
		return null;
	}

	return (
		<ModalButtonTrigger
			appearance="subtle"
			isDisabled={areButtonsDisabled || isSubTaskWithoutParent}
			onClick={onGicClick}
			entryPoint={gicModalEntryPoint}
			entryPointProps={{
				...gicEntryPointProps,
				projectId,
				selectedDraftWorkItem,
			}}
			entryPointParams={{
				cloudId: gicEntryPointProps.cloudId,
				parentIssue: (selectedDraftWorkItem?.fields?.parent as string | undefined) ?? undefined,
			}}
			modalProps={{
				width: 'large',
				shouldScrollInViewport: true,
			}}
			aria-label={
				FeatureGates.getExperimentValue('jira-aifc-a11y-fixes-preview-card', 'isEnabled', false, {
					fireExperimentExposure: false,
				})
					? formatMessage(messages.refineWorkItemAccessibleLabel)
					: undefined
			}
		>
			<FormattedMessage {...messages.refineInGic} />
		</ModalButtonTrigger>
	);
};

const PreviewButton = ({
	areButtonsDisabled,
	onPreviewClick,
}: {
	areButtonsDisabled: boolean;
	onPreviewClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) => {
	const { formatMessage } = useIntl();
	return (
		<Button
			onClick={onPreviewClick}
			isDisabled={areButtonsDisabled}
			appearance="subtle"
			aria-label={
				FeatureGates.getExperimentValue('jira-aifc-a11y-fixes-preview-card', 'isEnabled', false, {
					fireExperimentExposure: false,
				})
					? formatMessage(messages.openPreviewAccessibleLabel)
					: undefined
			}
		>
			<FormattedMessage {...messages.openPreview} />
		</Button>
	);
};

const getProduct = (product?: string): string => {
	switch (product) {
		case 'confluence':
			return 'confluence';
		case 'jira-software':
		case 'jira-core':
		case 'jsm':
		case 'jpd':
		case 'jira':
			return 'jira';
		case 'rovo':
		case 'atlas':
			return 'atlas';
		case 'bitbucket':
			return 'bitbucket';
		case 'admin':
			return 'admin';
		default:
			return 'unknown';
	}
};

/**
 * Actions shown in the footer of the chat preview card.
 */

export const PreviewFooterActions: React.MemoExoticComponent<
	({
		product,
		query,
		project,
		cloudId,
		isSingleWorkItem,
		areButtonsDisabled,
		onDismiss,
		onBulkAccept,
		onCreateSuccess,
		onCreateError,
		onPreviewClick,
		gicEntryPointProps,
		projectIdOrKey,
		isStagingArea,
		sendAifcAnalyticsEvent,
	}: PreviewFooterActionsProps) => React.JSX.Element
> = memo(
	({
		product,
		query,
		project,
		cloudId,
		messageId,
		isSingleWorkItem,
		areButtonsDisabled = false,
		onDismiss,
		onBulkAccept,
		onCreateSuccess,
		onCreateError,
		onPreviewClick,
		gicEntryPointProps,
		projectIdOrKey,
		isStagingArea,
		entryPoint,
		showSuccessFlagOnCreate,
		selectedItems,
		sendAifcAnalyticsEvent,
	}: PreviewFooterActionsProps): React.JSX.Element => {
		const { formatMessage } = useIntl();

		const tenantInfo = useFragment<EnabledKey>(
			graphql`
				fragment footerActions_AtlassianConversationAssistantPreviewCard_PreviewFooterActions_query on Query {
					jira_isBetaAiFeaturesEnabled(cloudId: $cloudId) @optIn(to: "JiraAtlassianIntelligence")
					tenantContexts(cloudIds: [$cloudId]) {
						cloudUrl
					}
				}
			`,
			query,
		);

		const cloudUrl = tenantInfo?.tenantContexts?.[0]?.cloudUrl ?? '';

		const projectData = useFragment<ProjectKey>(
			graphql`
				fragment footerActions_AtlassianConversationAssistantPreviewCard_PreviewFooterActions_project on JiraProject {
					projectId
				}
			`,
			project,
		);

		const isBetaAiFeaturesEnabled = tenantInfo?.jira_isBetaAiFeaturesEnabled ?? true;
		const isPreviewAifcEnabled =
			isBetaAiFeaturesEnabled && getProduct(product) === 'jira' && fg('jira_aifc_enabled');
		const projectId = projectData?.projectId ?? undefined;
		const draftWorkItemsToCreate = useGetDraftWorkItemsToCreate(messageId);
		const selectedDraftWorkItem = draftWorkItemsToCreate[0];

		const hasSelectedItems =
			FeatureGates.getExperimentValue<string>(
				'jira-aifc-workflow-resumption-ui-updates',
				'cohort',
				'control',
				{ fireExperimentExposure: false },
			) === 'checkboxes' &&
			selectedItems &&
			selectedItems?.size > 0;

		const createButtonLabel = useMemo(() => {
			if (isSingleWorkItem) {
				return <FormattedMessage {...messages.singleCreate} />;
			}
			if (hasSelectedItems) {
				const totalItemCount = draftWorkItemsToCreate.reduce(
					(count, item) => count + 1 + (item.childWorkItems?.length ?? 0),
					0,
				);
				if (selectedItems.size < totalItemCount) {
					return (
						<FormattedMessage {...messages.createCount} values={{ count: selectedItems.size }} />
					);
				}
			}
			return <FormattedMessage {...messages.createAll} />;
		}, [isSingleWorkItem, hasSelectedItems, draftWorkItemsToCreate, selectedItems]);

		const getActiveDraftWorkItems = useCallback(() => {
			let items: DraftWorkItem[] = draftWorkItemsToCreate;

			// Filter out unchecked issues. Child work items that are orphaned (ie. their parent was not checked)
			// are promoted to standalone work items.
			if (hasSelectedItems) {
				const promotedChildren: DraftWorkItem[] = [];
				items.forEach((item) => {
					if (selectedItems.has(item.invocationId)) {
						return;
					}
					item.childWorkItems?.forEach((child) => {
						if (selectedItems.has(child.invocationId) && !isSubTask(child)) {
							promotedChildren.push({
								...child,
								fields: { ...child.fields, parent: undefined },
								childWorkItems: [],
							});
						}
					});
				});

				items = items
					.filter((item) => selectedItems.has(item.invocationId))
					.map((item) => {
						if (item.childWorkItems) {
							return {
								...item,
								childWorkItems: item.childWorkItems.filter((child) =>
									selectedItems.has(child.invocationId),
								),
							};
						}
						return item;
					})
					.concat(promotedChildren);
			}

			return Promise.resolve(items);
		}, [draftWorkItemsToCreate, hasSelectedItems, selectedItems]);

		return (
			<Inline alignBlock="center" spread="space-between" alignInline="end" grow="fill">
				<DeclineButton
					areButtonsDisabled={areButtonsDisabled}
					isSingleWorkItem={isSingleWorkItem ?? false}
					onDismiss={onDismiss}
				/>

				<Inline space="space.100" alignBlock="center">
					{isSingleWorkItem ? (
						<EditInGicButton
							areButtonsDisabled={areButtonsDisabled}
							gicEntryPointProps={gicEntryPointProps}
							selectedDraftWorkItem={selectedDraftWorkItem}
							projectId={projectId}
							isStagingArea={isStagingArea}
							sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
						/>
					) : (
						isPreviewAifcEnabled && (
							<PreviewButton
								areButtonsDisabled={areButtonsDisabled}
								onPreviewClick={onPreviewClick}
							/>
						)
					)}

					<CreateWorkItemsButton
						getDraftWorkItems={getActiveDraftWorkItems}
						onSuccess={onCreateSuccess}
						onError={onCreateError}
						cloudId={cloudId}
						cloudUrl={cloudUrl}
						projectIdOrKey={projectIdOrKey}
						buttonContent={createButtonLabel}
						testId="jira-work-item-preview-card-create-button"
						isDisabled={areButtonsDisabled}
						showErrorsInFlag={false}
						showSuccessFlagOnCreate={showSuccessFlagOnCreate}
						onClick={onBulkAccept}
						isStagingArea={isStagingArea}
						entryPoint={entryPoint}
						ariaLabel={
							FeatureGates.getExperimentValue(
								'jira-aifc-a11y-fixes-preview-card',
								'isEnabled',
								false,
								{ fireExperimentExposure: false },
							)
								? formatMessage(
										isSingleWorkItem
											? messages.createWorkItemAccessibleLabel
											: messages.createAllWorkItemsAccessibleLabel,
									)
								: undefined
						}
						sendAifcAnalyticsEvent={sendAifcAnalyticsEvent}
					/>
				</Inline>
			</Inline>
		);
	},
);
